/**
 * Turns the `/pool/list-mine` response into the bulk-invite `Pools` tab's
 * list of pools the caller may invite out of (ISSUE-197, R1): only pools
 * where the caller is an owner or admin, is an active member, the pool
 * itself is active, and it is not the pool being invited into.
 *
 * It started as a copy of `apps/mobile/src/pools/invite/inviteSourcePools.ts`,
 * for the reason recorded at the top of `inviteBulkSelection.ts`. Since
 * ISSUE-1097 the web file also counts the other pools the fan is in but does
 * not run, so the empty state can say why nothing is listed. Mobile is frozen,
 * so the two files now differ on purpose.
 */

export type typeInviteSourcePoolInput = {
  poolId: string;
  name: string;
  poolRole: "owner" | "admin" | "member";
  membershipStatus: "pending" | "active" | "suspended";
  poolStatus: "active" | "archived";
};

export type typeInviteSourcePoolRow = { poolId: string; name: string };

export type typeInviteSourcePoolsState =
  | { phase: "loading" }
  | { phase: "failed"; errorCode: string | null }
  | { phase: "empty"; otherPoolCount: number }
  | { phase: "ready"; rows: typeInviteSourcePoolRow[] };

function isEligible(
  pool: typeInviteSourcePoolInput,
  targetPoolId: string,
): boolean {
  return (
    (pool.poolRole === "owner" || pool.poolRole === "admin") &&
    pool.membershipStatus === "active" &&
    pool.poolStatus === "active" &&
    pool.poolId !== targetPoolId
  );
}

export function resolveInviteSourcePoolsState(input: {
  isLoading: boolean;
  hasFailed: boolean;
  errorCode: string | null;
  items: readonly typeInviteSourcePoolInput[] | null;
  targetPoolId: string;
}): typeInviteSourcePoolsState {
  if (input.hasFailed) {
    return { phase: "failed", errorCode: input.errorCode };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.items === null) {
    return { phase: "loading" };
  }

  const rows = input.items
    .filter((pool) => isEligible(pool, input.targetPoolId))
    .map((pool) => ({ poolId: pool.poolId, name: pool.name }));

  if (rows.length === 0) {
    // None of these is one the caller runs (those would be rows above), so
    // this counts the pools the fan is in but cannot invite out of.
    const otherPoolCount = input.items.filter(
      (pool) =>
        pool.poolId !== input.targetPoolId &&
        pool.membershipStatus === "active" &&
        pool.poolStatus === "active",
    ).length;
    return { phase: "empty", otherPoolCount };
  }
  return { phase: "ready", rows };
}

/**
 * The `Pools` tab's empty-state title when the fan is in other pools but runs
 * none of them (ISSUE-1097, D4). `null` when they are in no other pool, so the
 * caller keeps the plain "No other pools to invite from".
 */
export function poolsEmptyReason(input: {
  otherPoolCount: number;
}): string | null {
  if (input.otherPoolCount <= 0) {
    return null;
  }
  if (input.otherPoolCount === 1) {
    return "You're in 1 other pool, but you can only invite from pools you run.";
  }
  return `You're in ${String(input.otherPoolCount)} other pools, but you can only invite from pools you run.`;
}
